import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  MouseEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { fr } from 'date-fns/locale';
import { Tooltip } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';

import dayjs from 'shared/helpers/dayjs';
import useClickOutside from 'shared/hooks/useClickOutside';
import { Arrow, Time } from 'icons/untitledui';
import { calendarPopperSx } from 'dashboard/styles/Filters.style';
import {
  ReflexQuestionAnswer,
  useExtractEntitiesMutation,
  useGenerateInstantReflexMutation,
  useSubmitReflexFeedbackMutation,
  useSubmitReflexQuestionAnswersMutation,
} from 'shared/hooks/api/shiftNotes';
import { usePosthogCapture } from 'shiftNotes/posthogCapture/shiftNotes';
import {
  InstantReflex,
  ReflexFeedbackRating,
  ReflexQuestion,
  ReflexStep,
} from 'shiftNotes/_types';
import * as Styled from 'shiftNotes/styles/NotesStack.style';

import StepRatingControls from '../ReflexFeedback/StepRatingControls';
import SubmissionResult from '../ReflexFeedback/SubmissionResult';
import SubmitStep from '../ReflexFeedback/SubmitStep';

import NoteCardExpandable from './NoteCardExpandable';

type AnswerValue = string | boolean | Date | null;
type Phase = 'editing' | 'regenerating' | 'thanks';

interface QuestionFeedback {
  rating: ReflexFeedbackRating | null;
  comment: string;
}

interface NoteCardMissingInfoSectionProps {
  instantReflex: InstantReflex;
  noteId: string;
  initiallyExpanded?: boolean;
  isSpecialist?: boolean;
}

const DEFAULT_DATE_FORMAT = 'DD/MM/YYYY';

function findAskForDetailsStep(
  instantReflex: InstantReflex
): ReflexStep | undefined {
  return instantReflex.steps?.find(
    (step) => step.step_type === 'ask_for_details'
  );
}

function seedAnswers(questions: ReflexQuestion[]): Record<string, AnswerValue> {
  return questions.reduce<Record<string, AnswerValue>>((acc, question) => {
    if (question.input_type === 'yes_no') {
      acc[question.id] =
        typeof question.answer_boolean === 'boolean'
          ? question.answer_boolean
          : null;
    } else if (question.input_type === 'date') {
      acc[question.id] = question.answer_date
        ? new Date(question.answer_date)
        : null;
    } else {
      acc[question.id] = question.answer_text ?? '';
    }
    return acc;
  }, {});
}

function buildPayload(
  questions: ReflexQuestion[],
  answers: Record<string, AnswerValue>
): ReflexQuestionAnswer[] {
  return questions.map((question) => {
    const value = answers[question.id];
    if (question.input_type === 'yes_no') {
      return {
        id: question.id,
        answer_boolean: typeof value === 'boolean' ? value : null,
      };
    }
    if (question.input_type === 'date') {
      const isValidDate =
        value instanceof Date && !Number.isNaN(value.getTime());
      return {
        id: question.id,
        answer_date: isValidDate ? dayjs(value).format('YYYY-MM-DD') : null,
      };
    }
    const trimmed = typeof value === 'string' ? value.trim() : '';
    return {
      id: question.id,
      answer_text: trimmed.length > 0 ? trimmed : null,
    };
  });
}

function BinaryInput({
  value,
  onChange,
  disabled = false,
}: {
  value: boolean | null;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}): JSX.Element {
  const { t } = useTranslation('pageShiftNotes');
  return (
    <Styled.BinaryRow>
      <Styled.BinaryButton
        $size="small"
        $selected={value === true}
        type="button"
        disabled={disabled}
        onClick={(e: MouseEvent) => {
          e.stopPropagation();
          onChange(true);
        }}
        aria-pressed={value === true}
      >
        {t('missingInfoCard.binary.yes')}
      </Styled.BinaryButton>
      <Styled.BinaryButton
        $size="small"
        $selected={value === false}
        type="button"
        disabled={disabled}
        onClick={(e: MouseEvent) => {
          e.stopPropagation();
          onChange(false);
        }}
        aria-pressed={value === false}
      >
        {t('missingInfoCard.binary.no')}
      </Styled.BinaryButton>
    </Styled.BinaryRow>
  );
}

function DateInput({
  value,
  onChange,
  disabled = false,
}: {
  value: Date | null;
  onChange: (next: Date | null) => void;
  disabled?: boolean;
}): JSX.Element {
  const { t, i18n } = useTranslation('pageShiftNotes');
  const locale = i18n.language === 'fr' ? fr : undefined;
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const popperRef = useRef<HTMLDivElement>(null);

  const displayValue = value
    ? dayjs(value).format(DEFAULT_DATE_FORMAT)
    : t('missingInfoCard.date.placeholder');

  const handleClose = useCallback(() => setOpen(false), []);
  useClickOutside([anchorRef, popperRef], handleClose, open);

  return (
    <>
      <Styled.DatePill
        ref={anchorRef}
        type="button"
        disabled={disabled}
        onClick={(e: MouseEvent) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
        aria-label={t('missingInfoCard.date.selectDate')}
      >
        <Styled.DatePillValue $isPlaceholder={!value}>
          {displayValue}
        </Styled.DatePillValue>
        <Styled.DatePillIcon aria-hidden="true">
          <Time.Calendar />
        </Styled.DatePillIcon>
      </Styled.DatePill>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
        <DesktopDatePicker
          value={value}
          onChange={(next) => {
            onChange(next);
            setOpen(false);
          }}
          open={open}
          onClose={handleClose}
          slots={{
            leftArrowIcon: Arrow.ChevronLeft,
            rightArrowIcon: Arrow.ChevronRight,
          }}
          slotProps={{
            textField: {
              sx: {
                width: 0,
                height: 0,
                overflow: 'hidden',
                '& .MuiOutlinedInput-root': {
                  display: 'none',
                },
              },
            },
            popper: {
              ref: popperRef,
              anchorEl: anchorRef.current,
              placement: 'bottom-start',
              sx: calendarPopperSx,
            },
          }}
        />
      </LocalizationProvider>
    </>
  );
}

function TextInput({
  value,
  onChange,
  disabled = false,
}: {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}): JSX.Element {
  const { t } = useTranslation('pageShiftNotes');
  return (
    <Styled.TextArea
      value={value}
      disabled={disabled}
      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
        onChange(e.target.value)
      }
      onClick={(e: MouseEvent) => e.stopPropagation()}
      onKeyDown={(e: React.KeyboardEvent) => e.stopPropagation()}
      placeholder={t('missingInfoCard.text.placeholder')}
      aria-label={t('missingInfoCard.text.ariaLabel')}
    />
  );
}

export default function NoteCardMissingInfoSection({
  instantReflex,
  noteId,
  initiallyExpanded = false,
  isSpecialist = false,
}: NoteCardMissingInfoSectionProps): JSX.Element | null {
  const { t } = useTranslation('pageShiftNotes');
  const shiftNotesCapture = usePosthogCapture();

  const step = findAskForDetailsStep(instantReflex);
  const questions = useMemo(
    () => [...(step?.questions ?? [])].sort((a, b) => a.order - b.order),
    [step?.questions]
  );

  const [isExpanded, setIsExpanded] = useState(initiallyExpanded);
  const [currentStep, setCurrentStep] = useState(1);
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>(() =>
    seedAnswers(questions)
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<Record<string, QuestionFeedback>>(
    {}
  );
  const [overallComment, setOverallComment] = useState('');
  const [phase, setPhase] = useState<Phase>('editing');
  const [hasVisitedSubmitStep, setHasVisitedSubmitStep] = useState(false);

  useEffect(() => {
    const total = questions.length + (isSpecialist ? 1 : 0);
    if (isSpecialist && currentStep >= total) {
      setHasVisitedSubmitStep(true);
    }
  }, [currentStep, questions.length, isSpecialist]);

  const { mutate: submitAnswers, isPending: isSubmitting } =
    useSubmitReflexQuestionAnswersMutation();
  const { mutate: extractEntities } = useExtractEntitiesMutation();
  const { mutate: generateInstantReflex } = useGenerateInstantReflexMutation();
  const { mutate: submitFeedback, isPending: isSubmittingFeedback } =
    useSubmitReflexFeedbackMutation();

  if (!step || questions.length === 0) return null;

  const latestVersion = instantReflex.version ?? 1;
  const totalSteps = questions.length + (isSpecialist ? 1 : 0);
  const isFeedbackStep = isSpecialist && currentStep >= totalSteps;
  const activeQuestion = isFeedbackStep ? null : questions[currentStep - 1];
  const activeValue = activeQuestion ? answers[activeQuestion.id] : null;
  const isLastStep = currentStep >= totalSteps;
  const isPending = isSubmitting || isSubmittingFeedback;
  const isLocked = phase !== 'editing' || isPending;
  const allRated =
    !isSpecialist ||
    questions.every((question) => feedback[question.id]?.rating != null);

  const stepsCompletedStatus = isSpecialist
    ? [
        ...questions.map((question) => feedback[question.id]?.rating != null),
        hasVisitedSubmitStep,
      ]
    : undefined;

  const handleAnswerChange = (next: AnswerValue) => {
    if (!activeQuestion) return;
    setAnswers((prev) => ({ ...prev, [activeQuestion.id]: next }));
  };

  const handleRatingChange = (
    questionId: string,
    rating: ReflexFeedbackRating
  ) => {
    setFeedback((prev) => ({
      ...prev,
      [questionId]: { rating, comment: prev[questionId]?.comment ?? '' },
    }));
    shiftNotesCapture.noteReflexFeedbackRated({
      shift_note_id: noteId,
      reflex_id: instantReflex.id,
      question_id: questionId,
      rating,
      version: latestVersion,
    });
    if (rating === 'positive' && !isLocked) {
      setCurrentStep((s) => Math.min(totalSteps, s + 1));
    }
  };

  const handleCommentChange = (questionId: string, comment: string) => {
    setFeedback((prev) => ({
      ...prev,
      [questionId]: { rating: prev[questionId]?.rating ?? null, comment },
    }));
  };

  const handleSubmitFeedback = () => {
    setErrorMessage(null);
    const negativeCount = questions.filter(
      (question) => feedback[question.id]?.rating === 'negative'
    ).length;
    const aggregatedRating: ReflexFeedbackRating =
      negativeCount > 0 ? 'negative' : 'positive';
    const aggregatedComment = questions
      .map((question) => {
        const comment = feedback[question.id]?.comment.trim();
        return comment ? `${question.question}\n${comment}` : null;
      })
      .filter((entry): entry is string => entry !== null)
      .join('\n\n');
    const items = [
      {
        step_id: step.id,
        rating: aggregatedRating,
        comment: aggregatedComment || null,
      },
    ];
    const trimmedOverall = overallComment.trim();
    const willRegenerate = negativeCount > 0 || trimmedOverall !== '';

    if (willRegenerate) setPhase('regenerating');

    submitFeedback(
      {
        reflexId: instantReflex.id,
        noteId,
        section: 'missing_info',
        overallComment: trimmedOverall || null,
        items,
      },
      {
        onSuccess: (data) => {
          shiftNotesCapture.noteReflexFeedbackSubmitted({
            shift_note_id: noteId,
            reflex_id: instantReflex.id,
            version: latestVersion,
            section: 'missing_info',
            negative_count: negativeCount,
            has_overall_comment: trimmedOverall !== '',
            regenerated: data.regenerated,
          });
          if (!data.regenerated) setPhase('thanks');
        },
        onError: () => {
          shiftNotesCapture.noteReflexFeedbackSubmitError({
            shift_note_id: noteId,
            reflex_id: instantReflex.id,
            version: latestVersion,
            section: 'missing_info',
          });
          setErrorMessage(t('reflexFeedback.submitError'));
          setPhase('editing');
        },
      }
    );
  };

  const handleSubmit = () => {
    setErrorMessage(null);
    if (isSpecialist) {
      handleSubmitFeedback();
      return;
    }
    const captureProperties = {
      shift_note_id: noteId,
      reflex_id: instantReflex.id,
      step_id: step.id,
    };
    submitAnswers(
      { stepId: step.id, reflexQuestions: buildPayload(questions, answers) },
      {
        onSuccess: () => {
          shiftNotesCapture.noteReflexMissingInfoAnswered(captureProperties);
          extractEntities(noteId);
          generateInstantReflex(noteId);
        },
        onError: () => {
          shiftNotesCapture.noteReflexMissingInfoAnswerError(captureProperties);
          setErrorMessage(t('missingInfoCard.submitError'));
        },
      }
    );
  };

  const handleNext = () => {
    if (isLastStep) {
      handleSubmit();
      return;
    }
    setCurrentStep((s) => Math.min(totalSteps, s + 1));
  };

  const handlePrev = () => {
    setCurrentStep((s) => Math.max(1, s - 1));
  };

  const handleToggle = () => setIsExpanded((v) => !v);

  const renderInput = (question: ReflexQuestion): JSX.Element => {
    const input = (() => {
      if (question.input_type === 'yes_no') {
        return (
          <BinaryInput
            value={activeValue as boolean | null}
            onChange={handleAnswerChange}
            disabled={isSpecialist}
          />
        );
      }
      if (question.input_type === 'date') {
        return (
          <DateInput
            value={(activeValue as Date | null) ?? null}
            onChange={handleAnswerChange}
            disabled={isSpecialist}
          />
        );
      }
      return (
        <TextInput
          value={(activeValue as string) ?? ''}
          onChange={handleAnswerChange}
          disabled={isSpecialist}
        />
      );
    })();

    if (!isSpecialist) return input;
    return (
      <Tooltip
        title={t('missingInfoCard.disabledTooltip')}
        arrow
        followCursor
        slotProps={{
          popper: {
            modifiers: [{ name: 'offset', options: { offset: [0, 20] } }],
          },
        }}
      >
        <Styled.DisabledInputWrapper>{input}</Styled.DisabledInputWrapper>
      </Tooltip>
    );
  };

  const label = isFeedbackStep
    ? t('reflexFeedback.missingInfoSubmitTitle')
    : `${t('missingInfoCard.title')} ${currentStep}/${totalSteps}`;

  const showResult = phase !== 'editing' && isFeedbackStep;

  const body = (() => {
    if (showResult) {
      return (
        <SubmissionResult
          state={phase === 'thanks' ? 'thanks' : 'regenerating'}
        />
      );
    }
    if (isFeedbackStep || !activeQuestion) {
      return (
        <SubmitStep
          overallComment={overallComment}
          onOverallCommentChange={setOverallComment}
          disabled={isLocked}
        />
      );
    }
    return (
      <>
        <Styled.ExpandableQuestion>
          {activeQuestion.question}
        </Styled.ExpandableQuestion>
        {renderInput(activeQuestion)}
        {isSpecialist && (
          <StepRatingControls
            rating={feedback[activeQuestion.id]?.rating ?? null}
            comment={feedback[activeQuestion.id]?.comment ?? ''}
            onRatingChange={(rating) =>
              handleRatingChange(activeQuestion.id, rating)
            }
            onCommentChange={(comment) =>
              handleCommentChange(activeQuestion.id, comment)
            }
            disabled={isLocked}
          />
        )}
      </>
    );
  })();

  return (
    <NoteCardExpandable
      variant="missing-info"
      collapsedLabel={`${currentStep}/${totalSteps}`}
      expandedLabel={label}
      currentStep={currentStep}
      totalSteps={totalSteps}
      isExpanded={isExpanded}
      onToggle={handleToggle}
      collapsible={!isSpecialist}
      onPrev={handlePrev}
      onNext={handleNext}
      canGoPrev={currentStep > 1 && !isLocked}
      canGoNext={!isLocked && (!isSpecialist || !isLastStep || allRated)}
      isLastStep={isLastStep}
      showPagination={phase !== 'regenerating'}
      headerPreview={activeQuestion?.question}
      stepsCompletedStatus={stepsCompletedStatus}
    >
      {body}
      {phase === 'editing' && errorMessage && (
        <Styled.ExpandableErrorMessage role="alert">
          {errorMessage}
        </Styled.ExpandableErrorMessage>
      )}
    </NoteCardExpandable>
  );
}
